import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import type { Session, User } from "@supabase/supabase-js";
import { useQueryClient } from "@tanstack/react-query";
import { useRouter } from "@tanstack/react-router";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

type AuthCtx = {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signOut: () => Promise<void>;
};

const Ctx = createContext<AuthCtx | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const queryClient = useQueryClient();
  const router = useRouter();
  const expiryTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearExpiryWarning = () => {
    if (expiryTimeoutRef.current) {
      clearTimeout(expiryTimeoutRef.current);
      expiryTimeoutRef.current = null;
    }
  };

  const scheduleExpiryWarning = (s: Session | null) => {
    clearExpiryWarning();
    if (!s?.expires_at) return;

    const expiresAtMs = s.expires_at * 1000;
    const warnAtMs = expiresAtMs - 5 * 60 * 1000; // 5 min before
    const delay = warnAtMs - Date.now();

    if (delay <= 0) return; // already within 5 min, skip warning

    expiryTimeoutRef.current = setTimeout(() => {
      toast("Your session expires soon — click to stay signed in", {
        duration: 60_000,
        action: {
          label: "Stay signed in",
          onClick: async () => {
            const { error } = await supabase.auth.refreshSession();
            if (error) {
              toast.error("Failed to refresh session. Please sign in again.");
            } else {
              toast.success("Session refreshed");
            }
          },
        },
      });
    }, delay);
  };

  useEffect(() => {
    // Set listener FIRST to avoid missing events
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, s) => {
      setSession(s);
      scheduleExpiryWarning(s);
      if (_event === "SIGNED_OUT") {
        clearExpiryWarning();
      }
      // Invalidate caches on auth state change
      queryClient.invalidateQueries();
      router.invalidate();
    });

    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
      scheduleExpiryWarning(data.session);
      setLoading(false);
    });

    return () => {
      subscription.unsubscribe();
      clearExpiryWarning();
    };
  }, [queryClient, router]);

  const value: AuthCtx = {
    user: session?.user ?? null,
    session,
    loading,
    signOut: async () => {
      clearExpiryWarning();
      await supabase.auth.signOut();
    },
  };

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
